import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const STORAGE_KEY = "cms_portal_ui_v1";

export const defaultUiPrefs = {
  /** "light" | "dark" | "system" */
  theme: "system",
  reduceMotion: false,
  /** "normal" | "large" — scales root font slightly */
  fontScale: "normal",
};

function readStoredPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultUiPrefs };
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { ...defaultUiPrefs };
    return { ...defaultUiPrefs, ...parsed };
  } catch {
    return { ...defaultUiPrefs };
  }
}

const UiPreferencesContext = createContext(null);

function applyDomClasses(prefs) {
  const root = document.documentElement;
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const dark =
    prefs.theme === "dark" || (prefs.theme === "system" && prefersDark);
  root.classList.toggle("dark", dark);
  root.classList.toggle("cms-reduce-motion", Boolean(prefs.reduceMotion));
  root.classList.toggle("cms-font-large", prefs.fontScale === "large");
}

export function UiPreferencesProvider({ children }) {
  const [prefs, setPrefsState] = useState(readStoredPrefs);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  const setPrefs = useCallback((patchOrFn) => {
    setPrefsState((prev) => {
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    applyDomClasses(prefs);
  }, [prefs]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSchemeChange = () => {
      if (prefsRef.current.theme === "system") {
        applyDomClasses(prefsRef.current);
      }
    };
    mq.addEventListener("change", onSchemeChange);
    return () => mq.removeEventListener("change", onSchemeChange);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return;
      try {
        const next = JSON.parse(e.newValue);
        setPrefsState({ ...defaultUiPrefs, ...next });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ prefs, setPrefs }), [prefs, setPrefs]);

  return (
    <UiPreferencesContext.Provider value={value}>
      {children}
    </UiPreferencesContext.Provider>
  );
}

export function useUiPreferences() {
  const ctx = useContext(UiPreferencesContext);
  if (!ctx) {
    return {
      prefs: defaultUiPrefs,
      setPrefs: () => {},
    };
  }
  return ctx;
}
